/**
 * Live Quiz Host Page
 *
 * Page component for hosting a real-time interactive live quiz session.
 * Creates a session and renders the HostDashboard component.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { HostDashboard } from '../components/live-quiz/host/HostDashboard';
import type { CreateSessionResponse } from '../types/liveQuiz';

type PageState = 'loading' | 'creating' | 'ready' | 'error';

export function LiveQuizHostPage() {
  const { formId, sessionId: urlSessionId } = useParams<{ formId?: string; sessionId?: string }>();
  const navigate = useNavigate();
  const { user, accessToken } = useAuthStore();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [sessionId, setSessionId] = useState<string | null>(urlSessionId || null);
  const [error, setError] = useState<string | null>(null);

  // Create a new session if we have a formId but no sessionId
  useEffect(() => {
    if (urlSessionId) {
      setSessionId(urlSessionId);
      setPageState('ready');
      return;
    }

    if (!formId) {
      setError('No form ID provided');
      setPageState('error');
      return;
    }

    if (!user || !accessToken) {
      navigate('/login', { state: { from: `/live/host/${formId}` } });
      return;
    }

    const createSession = async () => {
      setPageState('creating');
      try {
        const response = await fetch('/api/live-quiz/sessions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ formId }),
        });

        if (!response.ok) {
          const errorData = await response.json() as { error?: string };
          throw new Error(errorData.error || 'Failed to create session');
        }

        const data = await response.json() as CreateSessionResponse;
        setSessionId(data.sessionId);
        setPageState('ready');

        // Update URL to include session ID for sharing/refresh
        navigate(`/live/session/${data.sessionId}/host`, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create session');
        setPageState('error');
      }
    };

    createSession();
  }, [formId, urlSessionId, user, accessToken, navigate]);

  const handleEnd = () => {
    navigate('/formspace');
  };

  // Loading state
  if (pageState === 'loading' || pageState === 'creating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-purple-400 border-t-transparent animate-spin" />
          <h2 className="text-xl font-bold text-white mb-2">
            {pageState === 'creating' ? 'Creating Game Session...' : 'Loading...'}
          </h2>
          <p className="text-purple-300">Please wait</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-red-400 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/formspace')}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ready state - render the host dashboard
  if (!sessionId || !user) {
    return null;
  }

  return (
    <HostDashboard
      sessionId={sessionId}
      hostUserId={user.id}
      onEnd={handleEnd}
    />
  );
}

export default LiveQuizHostPage;
